/**
 * First-login onboarding tour — sequential, multi-step walkthrough that
 * surfaces the moment a brand-new user lands on the dashboard.
 *
 * Storage:
 *   - Server is the source of truth (collection: `onboarding_state`).
 *     The user's tour state syncs across devices via:
 *       GET  /api/admin/onboarding-state         — read
 *       POST /api/admin/onboarding-state         — upsert {step, completed, skipped}
 *       POST /api/admin/onboarding-state/restart — clear flags
 *   - localStorage `smarto.onboarding.state` mirrors the server state and
 *     is used as a fast cache so the tour decision happens before the API
 *     call resolves (prevents a flash of tour on slow connections).
 *
 * Steps live here (not in recentFeatures.js) because they're cross-page
 * fundamentals, not "what shipped recently". Each step references a path
 * + targetTestId; the tour navigates between them as the user clicks
 * "Next".
 */
import api from '@/lib/api';

const STORAGE_KEY = 'smarto.onboarding.state';

export const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    path: '/dashboard',
    targetTestId: 'dashboard-title',
    title: 'Welcome to Smarto 👋',
    hint: 'Your business overview lives here. Sales, expenses, profit, cash + bank, and AI insights — all in one place.',
    cta: 'Show me around',
  },
  {
    id: 'quick-actions',
    path: '/dashboard',
    targetTestId: 'quick-actions-widget',
    title: 'Record entries in seconds',
    hint: 'These shortcut buttons let you log a sale, expense, supplier payment, or new invoice in one click.',
    cta: 'Next',
  },
  {
    id: 'whats-new',
    path: '/dashboard',
    targetTestId: 'whats-new-button',
    title: 'Stay current',
    hint: "When we ship new features, they show up here. Click the sparkle any time you've been away to see what changed.",
    cta: 'Next',
  },
  {
    id: 'bank-statements',
    path: '/bank-statements',
    targetTestId: 'bankstatements-title',
    title: 'Auto-match bank deposits',
    hint: 'Drag any CSV / XLSX bank statement here. The smart-match engine pairs each row with the right sale, expense, or supplier — automatically.',
    cta: 'Next',
  },
  {
    id: 'route-health',
    path: '/route-health',
    targetTestId: 'render-error-top-paths',
    title: 'Health monitoring built-in',
    hint: 'Route Health probes every page + API. Client-side render errors land here within seconds — no Sentry needed.',
    cta: 'Finish',
    isLast: true,
  },
];

// ─── Local cache (read-side, fast path) ───────────────────────────────

export function getOnboardingState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { step: 0 };
  } catch (_) {
    return { step: 0 };
  }
}

export function setOnboardingState(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_) { /* ignore */ }
}

export function isOnboardingComplete() {
  const s = getOnboardingState();
  return Boolean(s.completed_at || s.skipped_at);
}

// ─── Server sync ──────────────────────────────────────────────────────

/** Pull the canonical state from the server and overwrite local cache.
 *  Fails silently (network/auth) so the tour falls back to local-only mode. */
export async function syncOnboardingStateFromServer() {
  try {
    const res = await api.get('/admin/onboarding-state');
    if (res?.data && typeof res.data === 'object') {
      const server = {
        step: res.data.step ?? 0,
        completed_at: res.data.completed_at || null,
        skipped_at: res.data.skipped_at || null,
      };
      setOnboardingState(server);
      return server;
    }
  } catch (_) { /* offline / unauth → keep local */ }
  return getOnboardingState();
}

async function _push(body) {
  try { await api.post('/admin/onboarding-state', body); } catch (_) { /* ignore */ }
}

export function advanceStep() {
  const s = getOnboardingState();
  const nextIdx = (s.step ?? 0) + 1;
  if (nextIdx >= ONBOARDING_STEPS.length) {
    const completedAt = new Date().toISOString();
    setOnboardingState({ ...s, step: nextIdx, completed_at: completedAt });
    _push({ step: nextIdx, completed: true });
  } else {
    setOnboardingState({ ...s, step: nextIdx });
    _push({ step: nextIdx });
  }
}

export function goToStep(idx) {
  const s = getOnboardingState();
  const safe = Math.max(0, Number(idx) || 0);
  setOnboardingState({ ...s, step: safe });
  _push({ step: safe });
}

export function skipOnboarding() {
  const s = getOnboardingState();
  const skippedAt = new Date().toISOString();
  setOnboardingState({ ...s, skipped_at: skippedAt });
  _push({ skipped: true });
}

export function restartOnboarding() {
  setOnboardingState({ step: 0 });
  // Best-effort server reset; ignore failures
  api.post('/admin/onboarding-state/restart').catch(() => {});
}
